import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Search, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const MyClimatePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/oszwxqrk_MyCClimate%20Project%20Landscape%20Fieldwork%20Earthkeepers.jpg)',
            backgroundPosition: 'center 60%'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              MyCClimate
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-blue-100">
              Community Responses to Climate Change in Highland Asia
            </p>
            <p className="text-xl text-blue-50 leading-relaxed">
              Understanding local perceptions and adaptation strategies in border communities
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About the Project</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Highland Institute is part of a research team conducting academic training and ethnographic 
                research in areas along the Northeast India-Myanmar border, studying perceptions and impacts of 
                climate change linked to natural resource use and environmental concerns within border communities.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Funded by the <strong>Danish Institute for International Studies</strong>, the MyCClimate project 
                explores community-driven strategies addressing climate impacts on local livelihoods and ecosystems 
                in Highland Asia, emphasizing resilience, adaptation, and indigenous environmental knowledge.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The overall objective is to strengthen academic and policy understandings of the linkages between 
                climate change actions and conflict dynamics in Myanmar's ethnic border regions and build research 
                capacity that contributes to sustainable peace and development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Research Focus Areas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Border Communities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Ethnographic research in India-Myanmar border regions examining climate perceptions in 
                  conflict-affected areas
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Community Adaptation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  How local communities view and adapt to climate change through traditional and innovative practices
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Natural Resource Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Environmental conservation, extraction, land use patterns, and pollution in highland ecosystems
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Customary Land Use</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Documentation of traditional land management systems and their role in climate resilience
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Institutions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Project Partners</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Danish Institute for International Studies (DIIS)</h3>
                <p className="text-gray-600 text-sm">Project Coordinator & Funding Organization</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Regional Centre for Social Science (RCSD)</h3>
                <p className="text-gray-600 text-sm">Chiang Mai University, Thailand</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Nyan Corridor</h3>
                <p className="text-gray-600 text-sm">Myanmar Research Partner</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">The Highland Institute</h3>
                <p className="text-gray-600 text-sm">Northeast India Research Hub</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Research Methodology</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                The MyCClimate project combines innovative research approaches to capture the complex relationships 
                between climate change, conflict, and community resilience:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Digital Ethnographic Methods:</strong> Online community engagement and remote data collection
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>In Situ Fieldwork:</strong> Ground-level research in border communities
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Local Collaboration:</strong> Partnership with indigenous researchers and organizations
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Capacity Building:</strong> Academic training for early-career researchers from the region
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Publications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Featured Publication</h2>
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl">Community Climate Resilience Strategies</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  The MyCClimate project produces new empirical knowledge on how state actors, civil society 
                  organizations, and political movements across Myanmar's border regions address climate change 
                  and the interlinked fields of environmental conservation and sustainable development.
                </p>
                <a
                  href="https://journals.highlanderpress.org/index.php/Highlander/article/view/14/13"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                >
                  Read Full Article
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Learn More About Our Climate Research</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Explore how communities in Highland Asia are responding to climate challenges
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/research">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                All Research Projects
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Researchers
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyClimatePage;
